import { useMemo } from "react";
import { useLocation, useNavigate, useSearch } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AnunciosFiltersForm,
  countActiveFilters,
  emptyAnunciosFilters,
  type AnunciosFilters,
} from "@/components/admin/AnunciosFiltersForm";
import {
  decodePortalUrl,
  encodePortalUrl,
  filtersEqual,
} from "@/lib/portal-url";
import { usePortalFiltersSheet } from "@/contexts/portal-filters-sheet";
import { usePortalLookups } from "@/hooks/use-portal-lookups";

export function PortalFiltersSheet() {
  const { filtersOpen, setFiltersOpen } = usePortalFiltersSheet();
  const navigate = useNavigate();
  const location = useLocation();
  const search = useSearch({ strict: false }) as Record<string, unknown>;


  const { lookups, lookupsReady } = usePortalLookups();

  const basePath = useMemo(() => {
    return location.pathname.startsWith("/portal-vip") ? "/portal-vip" : "/portal";
  }, [location.pathname]);

  const segments = useMemo(() => {
    const prefix = basePath === "/portal-vip" ? /^\/portal-vip\/?/ : /^\/portal\/?/;
    const path = location.pathname.replace(prefix, "");
    if (!path || path === "favoritos") return [] as string[];
    return path.split("/").filter(Boolean);
  }, [location.pathname, basePath]);

  const filters: AnunciosFilters = useMemo(
    () => decodePortalUrl(segments, search, lookups),
    [segments, search, lookups],
  );

  const active = countActiveFilters(filters);

  const setFilters = (next: AnunciosFilters) => {
    if (!lookupsReady) return;
    if (filtersEqual(next, filters)) return;
    const { pathname, search: nextSearch } = encodePortalUrl(next, lookups, basePath);
    navigate({
      to: pathname as never,
      search: nextSearch as never,
      replace: true,
    });
  };

  return (
    <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0 z-[60]">
        <SheetHeader className="p-6 pb-0">
          <SheetTitle className="flex items-center gap-2">
            Filtros
            {active > 0 && (
              <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-[10px]">
                {active}
              </Badge>
            )}
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto pl-6 pr-[10px] pb-2 pt-4">
          <AnunciosFiltersForm
            filters={filters}
            onChange={setFilters}
            hideInternal
          />
        </div>
        <div className="border-t bg-background p-4 flex gap-3">
          {active > 0 && (
            <Button
              variant="ghost"
              className="flex-1"
              onClick={() => setFilters(emptyAnunciosFilters)}
            >
              Limpiar filtros
            </Button>
          )}
          <Button
            className={`${active > 0 ? "flex-1" : "w-full"} bg-primary text-primary-foreground hover:bg-primary/90`}
            onClick={() => setFiltersOpen(false)}
          >
            Ver locales
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
